const placeDetailsCache = new Map();
const ongoingFetches = new Map();

const getCityAndCountry = (addressComponents) => {
  let city = '';
  let country = '';

  addressComponents.forEach(component => {
    if (component.types.includes('locality') || component.types.includes('postal_town')) {
      city = component.long_name;
    }
    if (component.types.includes('country')) {
      country = component.short_name;
    }
    if (!city && component.types.includes('administrative_area_level_2')) {
      city = component.long_name;
    }
  });

  return { city, country };
};

const standardizePlaceDetails = (details) => {
  return {
    google_place_id: details.place_id || "",
    name: details.name || "",
    rating: details.rating !== undefined ? details.rating : null,
    user_ratings_total: details.user_ratings_total || null,
    address_components: details.address_components || [],
    formatted_address: details.formatted_address || "",
    photos: details.photos?.map(photo => ({
      photo_reference: photo.photo_reference || photo.getUrl(),
      height: photo.height || photo.height_pixels,
      width: photo.width || photo.width_pixels,
      html_attributions: photo.html_attributions || []
    })) || [],
    types: details.types || [],
    opening_hours: details.opening_hours ? details.opening_hours.weekday_text : [],
    website: details.website || null,
    price_level: details.price_level !== undefined ? details.price_level : null,
    business_status: details.business_status || null,
    latitude: typeof details.geometry.location.lat === 'function'
      ? details.geometry.location.lat()
      : details.geometry.location.lat,
    longitude: typeof details.geometry.location.lng === 'function'
      ? details.geometry.location.lng()
      : details.geometry.location.lng
  };
};

const fetchFromGoogleApi = (googleMapsAPI, placeId) => {
  return new Promise((resolve, reject) => {
    const placesService = new googleMapsAPI.places.PlacesService(document.createElement('div'));

    const detailsRequest = {
      placeId: placeId,
      fields: [
        'place_id',
        'name',
        'rating',
        'user_ratings_total',
        'address_components',
        'formatted_address',
        'photos',
        'types',
        'opening_hours.weekday_text',
        'website',
        'price_level',
        'business_status',
        'geometry.location',
      ],
    };

    placesService.getDetails(detailsRequest, (googlePlaceDetails, status) => {
      if (status === googleMapsAPI.places.PlacesServiceStatus.OK && googlePlaceDetails) {
        const standardizedDetails = standardizePlaceDetails(googlePlaceDetails);

        const { city, country } = getCityAndCountry(standardizedDetails.address_components || []);

        const photoUrl =
          standardizedDetails.photos && standardizedDetails.photos.length > 0
            ? standardizedDetails.photos[0].photo_reference
            : null;

        const placeInfo = {
          ...standardizedDetails,
          city,
          country,
          photoUrl,
        };

        placeDetailsCache.set(placeId, placeInfo);
        ongoingFetches.delete(placeId);

        resolve(placeInfo);
      } else {
        console.error(`Failed to fetch place details from Google API: ${status}`);
        ongoingFetches.delete(placeId);
        resolve(null);
      }
    });
  });
};

const fetchPlaceDetails = async (googleMapsAPI, placeId, apiClient) => {
  if (!googleMapsAPI || !placeId) return null;

  if (placeDetailsCache.has(placeId)) {
    // console.log('Getting from Cache')
    return placeDetailsCache.get(placeId);
  }

  if (ongoingFetches.has(placeId)) {
    return ongoingFetches.get(placeId);
  }

  // console.log('Fetching from API')

  const fetchPromise = fetchFromGoogleApi(googleMapsAPI, placeId)
    .then(placeDetails => {
      if (placeDetails) {
        if (apiClient) {
          return apiClient.post('/places', {
            place_id: placeId,
            latitude: placeDetails.latitude,
            longitude: placeDetails.longitude,
          }).then(() => placeDetails)
            .catch(error => {
              console.error('Error sending place details to backend:', error);
              return placeDetails;
            });
        }
        return placeDetails;
      }
      return null;
    });

  ongoingFetches.set(placeId, fetchPromise);
  return fetchPromise;
};

export { fetchPlaceDetails, placeDetailsCache };
